import { IAllIssueColumnStates, ISelectedGisMap } from "@mctypes/frontend.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAllFilter } from "@schemas/commonSchema";
import { IIssueViewEnum } from "@mcapps/shared";
import { ISortColumn, IStoredIssueColConfigs } from "@schemas/issueSchema";
import { ICapitalLetter, IOtherAttributesMathFormula } from "@schemas/otherAttributesSchema";
import * as R from "ramda";
import { addAppModalThunk } from "../../../global/globalSlice";
import { IssueViewEnum } from "../../../mc-shared/enums";
import { AppThunk } from "../../../store";
import {
  ISSUE_COLUMN_LS_KEY,
  ISSUE_DATAFILEDS_ENUM,
  ISSUE_VIEW_LS_KEY,
  IssueKanbanGroupingViewEnum,
} from "./issueConstants";
import { selectGetOpenIssueArgsModalSelector } from "./issueSelectors";

export const ISSUE_TABLE_VARIANT_STORAGE_KEY = "issueTableVariant";
export type IssueTableVariant = "legacy" | "refactored";

export type IssueDataFieldsObject = {
  [key in keyof typeof ISSUE_DATAFILEDS_ENUM]: boolean;
};

export type IIssueDropdownFilters = {
  [key in ISSUE_DATAFILEDS_ENUM]?: string[];
};

export interface IssueReducer {
  allFiltersIssueBoardId: string | null;
  selectedPnsFilterItem: string; // 1.1.1
  selectedPnsBoardFilterItem: string; // 1.1.1
  activeTableTemplateId: string | null;
  issueTableVariant: IssueTableVariant;
  activeView: IIssueViewEnum;
  kanbanGroupingView: IssueKanbanGroupingViewEnum;
  selectedGisMap: ISelectedGisMap;
  allColumnState: IAllIssueColumnStates;
  sortedCol: ISortColumn;
  activeDashboard: string;
  currentIssueBoardId: string | null; // Added to keep track of current issue board ID
  svarGant: {
    zoomLevel: "week" | "month";
    startDate: string | null;
    endDate: string | null;
    skipItemsWithoutEndDate: boolean;
  };
  dropdownFilters: IIssueDropdownFilters;
  miniSearchFreeText: string;
  taskAndOrIssueArgs: ("TASK" | "ISSUE")[];
  includeAllCompletedIssuesOnBoard: boolean;
  issueLogsCreatedAtRange: [string | null, string | null];
  issueLogsPageSize: number;
  issueLogsCurrentPage: number;
  mathFormulaConfigDraft: IOtherAttributesMathFormula | null;
  formulaCounterGroupIssuesModal: {
    issueId: string;
    formulaAttributeKey: ICapitalLetter;
  } | null;
}

// Helper functions to get initial dates
const getInitStartDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]; // Beginning of current month
};

const getInitEndDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().split("T")[0]; // End of next month
};

const filtersToInclude = [ISSUE_DATAFILEDS_ENUM.TAGS, ISSUE_DATAFILEDS_ENUM.PRIORITY, ISSUE_DATAFILEDS_ENUM.STATUS];

const emptyFilters: Record<ISSUE_DATAFILEDS_ENUM, string[]> = Object.values(filtersToInclude).reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {} as Record<ISSUE_DATAFILEDS_ENUM, string[]>);

const initialState: IssueReducer = {
  allFiltersIssueBoardId: null,
  selectedPnsFilterItem: null,
  selectedPnsBoardFilterItem: null,
  activeTableTemplateId: null,
  issueTableVariant: localStorage.getItem(ISSUE_TABLE_VARIANT_STORAGE_KEY) === "refactored" ? "refactored" : "legacy",
  activeView: (localStorage.getItem(ISSUE_VIEW_LS_KEY) as IssueViewEnum) || IssueViewEnum.TABLE,
  kanbanGroupingView: IssueKanbanGroupingViewEnum.STATUS,
  selectedGisMap: null,
  allColumnState: JSON.parse(localStorage.getItem(ISSUE_COLUMN_LS_KEY) || "{}"),
  sortedCol: null,
  activeDashboard: null,
  currentIssueBoardId: null,
  svarGant: {
    zoomLevel: "week",
    startDate: localStorage.getItem("svarGantStartDate") || getInitStartDate(),
    endDate: localStorage.getItem("svarGantEndDate") || getInitEndDate(),
    skipItemsWithoutEndDate: false,
  },
  dropdownFilters: {} as Record<ISSUE_DATAFILEDS_ENUM, string[]>,
  miniSearchFreeText: "",
  taskAndOrIssueArgs: ["ISSUE"],
  includeAllCompletedIssuesOnBoard: false,
  issueLogsCreatedAtRange: [null, null],
  issueLogsPageSize: 500,
  issueLogsCurrentPage: 1,
  mathFormulaConfigDraft: null,
  formulaCounterGroupIssuesModal: null,
};

export const setIssueAllFiltersIfNoActiveTemplateThunk =
  (filters: IAllFilter[]): AppThunk =>
  (dispatch: any, getState) => {
    const { activeTableTemplateId } = getState().issueReducerNew;
    if (activeTableTemplateId == null) {
      // dispatch(setIssueAllFiltersInRedux(filters));
    }
  };

export const updateColumnStateThunk = (columnState: IStoredIssueColConfigs[]): AppThunk => {
  return (dispatch, getState) => {
    const allColumnStateCopy = R.clone(getState().issueReducerNew.allColumnState);
    const issueBoardId = getState().issueReducerNew.currentIssueBoardId;
    const updatedData = {
      ...allColumnStateCopy,
      [issueBoardId]: columnState,
    };
    if (columnState === null) {
      delete updatedData[issueBoardId];
    }
    localStorage.setItem(ISSUE_COLUMN_LS_KEY, JSON.stringify(updatedData));
    dispatch(setIssueColumnStateInRedux(updatedData));
  };
};
export const issueSlice = createSlice({
  name: "issue",
  initialState,
  reducers: {
    setIssueActiveTableTemplateIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.activeTableTemplateId = action.payload;
    },
    setIssueTableVariantInRedux: (state, action: PayloadAction<IssueTableVariant>) => {
      localStorage.setItem(ISSUE_TABLE_VARIANT_STORAGE_KEY, action.payload);
      state.issueTableVariant = action.payload;
    },
    setIssueActiveViewInRedux: (state, action: PayloadAction<IIssueViewEnum>) => {
      localStorage.setItem(ISSUE_VIEW_LS_KEY, action.payload);
      state.activeView = action.payload;
    },
    setKanbanGroupingViewInRedux: (state, action: PayloadAction<IssueKanbanGroupingViewEnum>) => {
      state.kanbanGroupingView = action.payload;
    },
    resetIssueRedux: (state) => {
      state.activeTableTemplateId = null;
      state.kanbanGroupingView = IssueKanbanGroupingViewEnum.STATUS;
    },
    setIssueSelectedGisMapInRedux: (state, action: PayloadAction<ISelectedGisMap>) => {
      state.selectedGisMap = action.payload;
    },
    setIssueColumnStateInRedux: (state, action: PayloadAction<IAllIssueColumnStates>) => {
      state.allColumnState = action.payload;
    },
    setSelectedPnsFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsFilterItem = action.payload;
    },
    setIssueSortedColInRedux: (state, action: PayloadAction<ISortColumn>) => {
      state.sortedCol = action.payload;
    },
    setActiveDashboardInRedux: (state, action: PayloadAction<string>) => {
      state.activeDashboard = action.payload;
    },
    setIssueSelectedPnsBoardFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsBoardFilterItem = action.payload;
    },
    setCurrentIssueboardId: (state, action: PayloadAction<string | null>) => {
      if (state.currentIssueBoardId !== action.payload) {
        state.includeAllCompletedIssuesOnBoard = false;
      }
      state.currentIssueBoardId = action.payload;
    },
    setAllFiltersIssueBoardId: (state, action: PayloadAction<string | null>) => {
      state.allFiltersIssueBoardId = action.payload;
    },
    setSvarGantZoomLevelInRedux: (state, action: PayloadAction<"week" | "month">) => {
      state.svarGant.zoomLevel = action.payload;
    },
    setSvarGantStartDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newStartDate = action.payload;
      state.svarGant.startDate = newStartDate;
      localStorage.setItem("svarGantStartDate", newStartDate || "");
      if (!newStartDate) {
        state.svarGant.startDate = localStorage.getItem("svarGantStartDate") || getInitStartDate(); // Default to beginning of current month
      }
    },
    setSvarGantEndDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newEndDate = action.payload;
      state.svarGant.endDate = newEndDate;
      localStorage.setItem("svarGantEndDate", newEndDate || "");
      if (!newEndDate) {
        state.svarGant.endDate = localStorage.getItem("svarGantEndDate") || getInitEndDate(); // Default to end of next month
      }
    },
    setSvarGantSkipItemsWithoutEndDateInRedux: (state, action: PayloadAction<boolean>) => {
      state.svarGant.skipItemsWithoutEndDate = action.payload;
    },
    setIssueDropdownSingleDataFieldFilterInRedux: (
      state,
      action: PayloadAction<{ filter: ISSUE_DATAFILEDS_ENUM; values: string[] }>,
    ) => {
      const { filter, values } = action.payload;
      state.dropdownFilters[filter] = values;
    },
    setIssueDropdownFilterAllInRedux: (state, action: PayloadAction<{ filters: IIssueDropdownFilters }>) => {
      state.dropdownFilters = action.payload.filters;
    },
    clearAllDropdownFilterInRedux: (state) => {
      state.dropdownFilters = {};
    },
    setMiniSearchFreeTextInRedux: (state, action: PayloadAction<string>) => {
      state.miniSearchFreeText = action.payload;
    },
    setTaskAndOrIssueArgsInRedux: (state, action: PayloadAction<("TASK" | "ISSUE")[]>) => {
      state.taskAndOrIssueArgs = action.payload;
    },
    setIncludeAllCompletedIssuesOnBoardInRedux: (state, action: PayloadAction<boolean>) => {
      state.includeAllCompletedIssuesOnBoard = action.payload;
    },
    //Historik-tabben
    setIssueLogsCreatedAtRangeInRedux: (state, action: PayloadAction<[string | null, string | null]>) => {
      state.issueLogsCreatedAtRange = action.payload;
    },
    setIssueLogsPageSizeInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsPageSize = action.payload;
    },
    setIssueLogsCurrentPageInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsCurrentPage = action.payload;
    },
    setMathFormulaConfigDraftInRedux: (state, action: PayloadAction<IOtherAttributesMathFormula | null>) => {
      state.mathFormulaConfigDraft = action.payload;
    },
    resetMathFormulaConfigInRedux: (state) => {
      state.mathFormulaConfigDraft = null;
    },
    setFormulaCounterGroupIssuesModalInRedux: (
      state,
      action: PayloadAction<{ issueId: string; formulaAttributeKey: ICapitalLetter } | null>,
    ) => {
      state.formulaCounterGroupIssuesModal = action.payload;
    },
  },
});

export const thunkOpenIssueModalById =
  (issueId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const args = selectGetOpenIssueArgsModalSelector(state, issueId);
    if (args.projectId == null || args.boardId == null) {
      console.warn("Kan ikke åpne issue modal – mangler projectId eller boardId");
      return;
    }
    dispatch(addAppModalThunk(args));
  };

export const {
  setIssueActiveTableTemplateIdInRedux,
  setIssueTableVariantInRedux,
  setIssueActiveViewInRedux,
  setKanbanGroupingViewInRedux,
  resetIssueRedux,
  setIssueSelectedGisMapInRedux,
  setSelectedPnsFilterItemInRedux,
  setIssueSortedColInRedux,
  setActiveDashboardInRedux,
  setIssueColumnStateInRedux,
  setIssueSelectedPnsBoardFilterItemInRedux,
  setCurrentIssueboardId,
  setAllFiltersIssueBoardId,
  setSvarGantZoomLevelInRedux,
  setSvarGantStartDateInRedux,
  setSvarGantEndDateInRedux,
  setSvarGantSkipItemsWithoutEndDateInRedux,
  setIssueDropdownSingleDataFieldFilterInRedux,
  setMiniSearchFreeTextInRedux,
  setTaskAndOrIssueArgsInRedux,
  setIncludeAllCompletedIssuesOnBoardInRedux,
  clearAllDropdownFilterInRedux,
  setIssueDropdownFilterAllInRedux,
  setIssueLogsCreatedAtRangeInRedux,
  setIssueLogsPageSizeInRedux,
  setIssueLogsCurrentPageInRedux,
  setMathFormulaConfigDraftInRedux,
  resetMathFormulaConfigInRedux,
  setFormulaCounterGroupIssuesModalInRedux,
} = issueSlice.actions;

export default issueSlice.reducer;
